import React, { useEffect, useState } from 'react';
import { Button, Alert, Badge, Spinner, ListGroup } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { assetService } from '../../../services/assetService';
import { AssetHistoryDetail, AssetHistoryEntry } from '../../../types/Asset';

interface AssetHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  assetId: number;
  assetName?: string;
}

const stageLabel: Record<string, string> = {
  created: 'Created',
  data_edit: 'Data edited',
  status_change: 'Status changed',
  deleted: 'Deleted',
  manual_comment: 'Manual Comment',
};

const fieldLabel: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  assetType: 'Asset Type',
  assetNumber: 'Asset Number',
  subtype: 'Subtype',
  startLatitude: 'Start Latitude',
  startLongitude: 'Start Longitude',
  endLatitude: 'End Latitude',
  endLongitude: 'End Longitude',
  highwayUnique: 'Highway Unique',
  parentAsset: 'Parent Asset',
  contractAreaId: 'Contract Area',
  numberedRoad: 'Numbered Road',
  summerClass: 'Summer Class',
  winterClass: 'Winter Class',
  length: 'Length',
  status: 'Status',
};

const formatDetail = (d: AssetHistoryDetail): string => {
  const oldStr = d.oldValue === null || d.oldValue === undefined ? '(empty)' : String(d.oldValue);
  const newStr = d.newValue === null || d.newValue === undefined ? '(empty)' : String(d.newValue);
  const label = fieldLabel[d.field] ?? d.field;
  return `${label}: ${oldStr} -> ${newStr}`;
};

const getActorLabel = (entry: AssetHistoryEntry): string => {
  if (entry.user) {
    const name = `${entry.user.name} ${entry.user.lastName}`.trim();
    return `By ${name || 'Unknown'}`;
  }
  return 'By System';
};

const AssetHistoryModal: React.FC<AssetHistoryModalProps> = ({
  visible,
  onClose,
  assetId,
  assetName,
}) => {
  const [history, setHistory] = useState<AssetHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !assetId) {
      setHistory([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    assetService
      .getAssetHistory(assetId)
      .then((data) => {
        if (!cancelled) setHistory(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load asset history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, assetId]);

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={assetName ? `Asset history: ${assetName}` : 'Asset history'}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading ? (
        <div className="tw-text-center tw-py-4">
          <Spinner className="tw-text-primary">Loading...</Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : history.length > 0 ? (
        <ListGroup>
          {history.map((entry) => (
            <ListGroup.Item key={entry.id}>
              <div className="tw-flex tw-justify-between tw-items-start">
                <div className="tw-grow">
                  <h6 className="tw-mb-1">{entry.summary || stageLabel[entry.stage] || entry.stage}</h6>
                  <p className="tw-mb-1 tw-text-content-muted">
                    Stage: <Badge bg="secondary">{stageLabel[entry.stage] || entry.stage}</Badge>
                  </p>
                  {entry.details && entry.details.length > 0 && (
                    <div className="tw-mb-1">
                      {entry.details.map((d, idx) => (
                        <div key={idx} className="tw-text-sm">
                          {formatDetail(d)}
                        </div>
                      ))}
                    </div>
                  )}
                  <small className="tw-text-content-muted">{getActorLabel(entry)}</small>
                </div>
                <small className="tw-text-content-muted tw-ml-2">{new Date(entry.createdAt).toLocaleString()}</small>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <Alert variant="info">No history records found.</Alert>
      )}
    </ResponsiveModal>
  );
};

export default AssetHistoryModal;
